import jsPDF from 'jspdf';

// Brand colors
const BRAND_RED_RGB = { r: 223, g: 96, b: 83 }; // BOTW color
const BRAND_GREEN_RGB = { r: 141, g: 196, b: 169 }; // GTB color #8DC4A9
const BRAND_FIVEON_RGB = { r: 45, g: 127, b: 249 }; // Five On color #2D7FF9
const WHITE_RGB = { r: 255, g: 255, b: 255 };
const BLACK_RGB = { r: 0, g: 0, b: 0 };
const LIGHT_GRAY_RGB = { r: 200, g: 200, b: 200 };

// Get brand color based on app type
function getBrandColor(appType) {
    if (appType === 2) {
        return BRAND_GREEN_RGB;
    }
    if (appType === 3) {
        return BRAND_FIVEON_RGB;
    }
    return BRAND_RED_RGB;
}

// Page constants
const PAGE_WIDTH = 612; // Letter size in points
const PAGE_HEIGHT = 792;
const MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);

// Get app-specific copy and logo
function getAppCopy(appType) {
    if (appType === 2) {
        return {
            logoText: 'Guess The Baby',
            guestsLabel: 'TOTAL PARTICIPANTS',
            logoFile: '/payment_logo_gtb.png'
        };
    } else if (appType === 3) {
        return {
            logoText: 'Five On',
            guestsLabel: 'TOTAL PARTICIPANTS',
            logoFile: '/payment_logo_5on.png'
        };
    } else {
        return {
            logoText: 'BETTING ON THE Wedding',
            guestsLabel: 'TOTAL GUESTS',
            logoFile: '/payment_logo_botw.png'
        };
    }
}

// Format date
function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// Format number with leading zero
function formatNumber(num) {
    return num < 10 ? `0${num}` : `${num}`;
}

// Load image and convert to base64
function loadImageAsBase64(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
        };
        img.onerror = reject;
        img.src = url;
    });
}

// Draw rounded rectangle
function drawRoundedRect(doc, x, y, width, height, radius, fillColor) {
    doc.setFillColor(fillColor.r, fillColor.g, fillColor.b);
    doc.roundedRect(x, y, width, height, radius, radius, 'F');
}

// Get brand color for current document
function getDocBrandColor(appType) {
    return getBrandColor(appType);
}

// Draw page header (called on every page)
function drawPageHeader(doc, event, appCopy, pageNum, totalPages, logoBase64, brandColor) {
    // "OFFICIAL BETTING RECORD" title - moved down
    doc.setFontSize(14);
    doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
    doc.setFont('helvetica', 'bold');
    doc.text('OFFICIAL BETTING RECORD', MARGIN, 65);
    
    // Logo on right side - aligned with title
    if (logoBase64) {
        // Different dimensions per app to prevent stretching
        const isGTB = appCopy.logoFile === '/payment_logo_gtb.png';
        const isFiveOn = appCopy.logoFile === '/payment_logo_5on.png';
        const logoWidth = (isGTB || isFiveOn) ? 170 : 200;
        const logoHeight = 30;
        const logoX = PAGE_WIDTH - MARGIN - logoWidth;
        doc.addImage(logoBase64, 'PNG', logoX, 42, logoWidth, logoHeight);
    }
    
    // Event Name - always use event_title
    const names = event.event_title || 'Event';
    
    doc.setFontSize(14);
    doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
    doc.setFont('helvetica', 'bold');
    doc.text(names, MARGIN, 98);
    
    // Date/Location/ID line
    const dateStr = formatDate(event.event_date);
    const locationStr = event.event_location || '';
    const eventIdStr = `ID: ${event.event_id}`;
    const dateLocation = [dateStr, locationStr, eventIdStr].filter(Boolean).join(' • ');
    
    doc.setFontSize(8);
    doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
    doc.setFont('helvetica', 'normal');
    doc.text(dateLocation, MARGIN, 113);
    
    // Page number at bottom
    doc.setFontSize(8);
    doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
    doc.text(`PAGE | ${formatNumber(pageNum)}`, MARGIN, PAGE_HEIGHT - 25);
    
    return 145; // Return Y position after header - more space before VITALS
}

// Draw section title with icon
function drawSectionTitle(doc, title, iconType, x, y, isContinued = false, icons = {}) {
    const displayTitle = isContinued ? `${title} (CONTINUED)` : title;
    
    // Draw icon from image if available
    const iconSize = 14;
    const iconX = x;
    const iconY = y - 10;
    
    const iconMap = {
        'vitals': icons.vitals,
        'podium': icons.podium,
        'gameTape': icons.gameTape,
        'participants': icons.participants
    };
    
    if (iconMap[iconType]) {
        doc.addImage(iconMap[iconType], 'PNG', iconX, iconY, iconSize, iconSize);
    }
    
    // Title text
    doc.setFontSize(11);
    doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
    doc.setFont('helvetica', 'bold');
    doc.text(displayTitle, x + 20, y);
    
    // Underline - explicitly set to black
    const textWidth = doc.getTextWidth(displayTitle);
    doc.setDrawColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
    doc.setLineWidth(0.8);
    doc.line(x + 20, y + 2, x + 20 + textWidth, y + 2);
    
    return y + 18;
}

// Draw THE VITALS section
function drawVitalsSection(doc, vitals, appCopy, startY, icons, brandColor) {
    let y = drawSectionTitle(doc, 'THE VITALS', 'vitals', MARGIN, startY, false, icons);
    
    console.log('Drawing VITALS section:', {
        totalGuests: vitals.totalGuests,
        totalBets: vitals.totalBets,
        thePot: vitals.thePot,
        vitalsObject: vitals
    });
    
    const gap = 10;
    const boxWidth = (CONTENT_WIDTH - (gap * 2)) / 3;
    const boxHeight = 60;
    const boxRadius = 5;
    
    // Box 1: Total Guests
    drawRoundedRect(doc, MARGIN, y, boxWidth, boxHeight, boxRadius, brandColor);
    doc.setFontSize(7);
    doc.setTextColor(WHITE_RGB.r, WHITE_RGB.g, WHITE_RGB.b);
    doc.setFont('helvetica', 'bold');
    const label1 = appCopy.guestsLabel;
    doc.text(label1, MARGIN + boxWidth / 2, y + 16, { align: 'center' });
    doc.setFontSize(21);
    const guestsDisplayValue = formatNumber(vitals.totalGuests || 0);
    console.log('Displaying totalGuests:', guestsDisplayValue, 'from value:', vitals.totalGuests);
    doc.text(guestsDisplayValue, MARGIN + boxWidth / 2, y + 42, { align: 'center' });
    
    // Box 2: Total Bets Placed
    const box2X = MARGIN + boxWidth + gap;
    drawRoundedRect(doc, box2X, y, boxWidth, boxHeight, boxRadius, brandColor);
    doc.setFontSize(7);
    doc.text('TOTAL BETS PLACED', box2X + boxWidth / 2, y + 16, { align: 'center' });
    doc.setFontSize(21);
    doc.text(formatNumber(vitals.totalBets), box2X + boxWidth / 2, y + 42, { align: 'center' });
    
    // Box 3: The Pot
    const box3X = MARGIN + (boxWidth + gap) * 2;
    drawRoundedRect(doc, box3X, y, boxWidth, boxHeight, boxRadius, brandColor);
    doc.setFontSize(7);
    doc.text('THE POT', box3X + boxWidth / 2, y + 16, { align: 'center' });
    // Adaptive font size - 25% smaller for longer text like "Bragging Rights"
    let potFontSize = 12;
    if (vitals.thePot.length > 15) potFontSize = 8;
    else if (vitals.thePot.length > 10) potFontSize = 10;
    doc.setFontSize(potFontSize);
    doc.text(vitals.thePot, box3X + boxWidth / 2, y + 42, { align: 'center' });
    
    return y + boxHeight + 30;
}

// Draw THE PODIUM section
function drawPodiumSection(doc, podium, startY, icons, brandColor) {
    console.log('Drawing PODIUM section:', {
        podium: podium,
        champion: podium?.champion,
        runnerUp: podium?.runnerUp,
        bronze: podium?.bronze,
        woodenSpoon: podium?.woodenSpoon
    });
    
    let y = drawSectionTitle(doc, 'THE PODIUM', 'podium', MARGIN, startY, false, icons);
    
    const rowHeight = 32;
    const col1Width = 220;
    const col2Width = 180;
    
    const podiumEntries = [
        { key: 'champion', label: 'THE CHAMPION (1st Place)', data: podium?.champion },
        { key: 'runnerUp', label: 'THE RUNNER UP (2nd Place)', data: podium?.runnerUp },
        { key: 'bronze', label: 'THE BRONZE MEDAL (3rd Place)', data: podium?.bronze },
        { key: 'woodenSpoon', label: 'THE WOODEN SPOON (Last Place)', data: podium?.woodenSpoon }
    ];
    
    // Check if podium is completely empty
    const hasAnyData = podiumEntries.some(entry => entry.data);
    
    if (!hasAnyData) {
        // Draw empty state message
        doc.setFontSize(10);
        doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
        doc.setFont('helvetica', 'italic');
        doc.text('No participants placed bets for this event.', MARGIN, y + 20);
        return y + 50;
    }
    
    let currentY = y;
    podiumEntries.forEach((entry) => {
        if (!entry.data) return;

        // For the wooden spoon (last place), hide the row when percentage is 0
        // (often someone who joined but didn't answer any questions).
        if (entry.key === 'woodenSpoon') {
            const pct = Number(entry.data.percentage);
            if (Number.isNaN(pct) || pct === 0) return;
        }
        
        // Draw left border with brand color
        doc.setFillColor(brandColor.r, brandColor.g, brandColor.b);
        doc.rect(MARGIN, currentY, 4, rowHeight, 'F');
        
        // Draw row border (full rectangle)
        doc.setDrawColor(LIGHT_GRAY_RGB.r, LIGHT_GRAY_RGB.g, LIGHT_GRAY_RGB.b);
        doc.setLineWidth(0.5);
        doc.rect(MARGIN, currentY, CONTENT_WIDTH, rowHeight, 'S');
        
        // Draw vertical column dividers
        doc.line(MARGIN + col1Width, currentY, MARGIN + col1Width, currentY + rowHeight);
        doc.line(MARGIN + col1Width + col2Width, currentY, MARGIN + col1Width + col2Width, currentY + rowHeight);
        
        // Column 1: Label (left aligned with padding)
        doc.setFontSize(9);
        doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
        doc.setFont('helvetica', 'normal');
        doc.text(entry.label, MARGIN + 12, currentY + (rowHeight / 2) + 3);
        
        // Column 2: Name (left aligned within column)
        doc.setFontSize(10);
        doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
        doc.setFont('helvetica', 'bold');
        doc.text(entry.data.name || 'Unknown', MARGIN + col1Width + 12, currentY + (rowHeight / 2) + 3);
        
        // Column 3: % Correct (left aligned within column)
        doc.setFontSize(10);
        doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
        doc.setFont('helvetica', 'bold');
        const percentage = entry.data.percentage || 0;
        const percentageText = `${percentage}% Correct`;
        doc.text(percentageText, MARGIN + col1Width + col2Width + 12, currentY + (rowHeight / 2) + 3);
        
        currentY += rowHeight;
    });
    
    return currentY + 28;
}

// Game tape column widths
const GAME_TAPE_COL1_WIDTH = 280;
const GAME_TAPE_COL2_WIDTH = 120;

// Draw THE GAME TAPE section header row
function drawGameTapeHeader(doc, y, brandColor) {
    // Header row background
    doc.setFillColor(brandColor.r, brandColor.g, brandColor.b);
    doc.rect(MARGIN, y, CONTENT_WIDTH, 24, 'F');
    
    // Header text
    doc.setFontSize(9);
    doc.setTextColor(WHITE_RGB.r, WHITE_RGB.g, WHITE_RGB.b);
    doc.setFont('helvetica', 'bold');
    doc.text('THE QUESTION', MARGIN + 12, y + 16);
    doc.text('THE ANSWER', MARGIN + GAME_TAPE_COL1_WIDTH + 12, y + 16);
    doc.text('CROWD STATS', MARGIN + GAME_TAPE_COL1_WIDTH + GAME_TAPE_COL2_WIDTH + 12, y + 16);
    
    return y + 24;
}

// Draw a single game tape row
function drawGameTapeRow(doc, row, y, brandColor) {
    const rowHeight = 30;
    
    // Row bottom border only
    doc.setDrawColor(LIGHT_GRAY_RGB.r, LIGHT_GRAY_RGB.g, LIGHT_GRAY_RGB.b);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y + rowHeight, MARGIN + CONTENT_WIDTH, y + rowHeight);
    
    // Question text - same position as header
    doc.setFontSize(8);
    doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
    doc.setFont('helvetica', 'normal');
    
    // Truncate long questions
    let questionText = row.question || '';
    if (questionText.length > 55) {
        questionText = questionText.substring(0, 52) + '...';
    }
    doc.text(questionText, MARGIN + 12, y + 18);
    
    // Answer - same position as header
    doc.setFont('helvetica', 'bold');
    doc.text(row.answer || 'N/A', MARGIN + GAME_TAPE_COL1_WIDTH + 12, y + 18);
    
    // Crowd Stats - same position as header
    const crowdStatsText = `${row.percentageCorrect}% got this right`;
    doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(crowdStatsText, MARGIN + GAME_TAPE_COL1_WIDTH + GAME_TAPE_COL2_WIDTH + 12, y + 18);
    
    return rowHeight;
}

// Draw a single participants row (3 names per row)
function drawParticipantsRow(doc, names, y) {
    const colWidth = CONTENT_WIDTH / 3;
    const rowHeight = 30;
    
    doc.setDrawColor(LIGHT_GRAY_RGB.r, LIGHT_GRAY_RGB.g, LIGHT_GRAY_RGB.b);
    doc.setLineWidth(0.5);
    
    // Draw all 3 cells
    names.forEach((name, index) => {
        const x = MARGIN + (colWidth * index);
        doc.rect(x, y, colWidth, rowHeight, 'S');
        
        if (name) {
            doc.setFontSize(9);
            doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
            doc.setFont('helvetica', 'normal');
            doc.text(name, x + 12, y + 19);
        }
    });
    
    return rowHeight;
}

// Generate PDF
export async function generateEventSummaryPDF(summaryData) {
    try {
        console.log('=== Starting PDF Generation ===');
        console.log('Summary Data:', summaryData);
        
        // Validate required data
        if (!summaryData) {
            throw new Error('No summary data provided');
        }
        
        const { event, vitals, podium, gameTape, witnesses, appType } = summaryData;
        
        if (!event) {
            throw new Error('Event data is missing');
        }
        if (!vitals) {
            throw new Error('Vitals data is missing');
        }
        
        console.log('Creating PDF document...');
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'letter'
        });

        const appCopy = getAppCopy(appType || 1);
        const brandColor = getBrandColor(appType || 1);
        
        // Debug: Log data sizes
        console.log('PDF Generation - Data received:', {
            appType: appType,
            gameTapeCount: gameTape?.length || 0,
            witnessesCount: witnesses?.length || 0,
            totalGuests: vitals?.totalGuests || 0,
            podium: podium,
            witnesses: witnesses?.slice(0, 5) // First 5 for debugging
        });
    
    // Load logo and icons - with individual error handling
    let logoBase64 = null;
    const icons = {};
    
    try {
        console.log('Attempting to load logo:', appCopy.logoFile);
        logoBase64 = await loadImageAsBase64(appCopy.logoFile).catch(err => {
            console.warn('Could not load logo:', appCopy.logoFile, err);
            return null;
        });
    } catch (e) {
        console.warn('Error loading logo:', e);
    }
    
    try {
        console.log('Attempting to load icons...');
        const iconResults = await Promise.allSettled([
            loadImageAsBase64('/vitals.png'),
            loadImageAsBase64('/podium.png'),
            loadImageAsBase64('/game_tape.png'),
            loadImageAsBase64('/participants.png')
        ]);
        
        if (iconResults[0].status === 'fulfilled') icons.vitals = iconResults[0].value;
        if (iconResults[1].status === 'fulfilled') icons.podium = iconResults[1].value;
        if (iconResults[2].status === 'fulfilled') icons.gameTape = iconResults[2].value;
        if (iconResults[3].status === 'fulfilled') icons.participants = iconResults[3].value;
        
        console.log('Icons loaded:', Object.keys(icons));
    } catch (e) {
        console.warn('Error loading icons:', e);
    }
    
    // Calculate total pages needed (rough estimate for page numbering)
    const gameTapeRowsPerPage = 16;
    const participantsPerPage = 48; // 16 rows x 3 columns
    const gameTapePages = Math.ceil((gameTape?.length || 0) / gameTapeRowsPerPage);
    const participantPages = Math.ceil((witnesses?.length || 0) / participantsPerPage);
    
    let pageNum = 1;
    let totalPages = 1 + Math.max(0, gameTapePages - 1) + Math.max(0, participantPages - 1);
    
    // ========== PAGE 1 ==========
    console.log('Drawing page header...');
    let y = drawPageHeader(doc, event, appCopy, pageNum, totalPages, logoBase64, brandColor);
    
    // THE VITALS
    console.log('Drawing vitals section...');
    y = drawVitalsSection(doc, vitals, appCopy, y, icons, brandColor);
    
    // THE PODIUM
    console.log('Drawing podium section...');
    y = drawPodiumSection(doc, podium, y, icons, brandColor);
    
    // THE GAME TAPE (start)
    console.log('Drawing game tape section...');
    y = drawSectionTitle(doc, 'THE GAME TAPE', 'gameTape', MARGIN, y, false, icons);
    y = drawGameTapeHeader(doc, y, brandColor);
    
    let gameTapeIndex = 0;
    const gameTapeData = gameTape || [];
    
    // Draw game tape rows on first page
    while (gameTapeIndex < gameTapeData.length && y < PAGE_HEIGHT - 80) {
        const rowHeight = drawGameTapeRow(doc, gameTapeData[gameTapeIndex], y, brandColor);
        y += rowHeight;
        gameTapeIndex++;
    }
    
    // Continue GAME TAPE on additional pages if needed
    while (gameTapeIndex < gameTapeData.length) {
        doc.addPage();
        pageNum++;
        console.log(`Added page ${pageNum}, total pages in doc: ${doc.internal.getNumberOfPages()}`);
        y = drawPageHeader(doc, event, appCopy, pageNum, totalPages, logoBase64, brandColor);
        
        y = drawSectionTitle(doc, 'THE GAME TAPE', 'gameTape', MARGIN, y, true, icons);
        y = drawGameTapeHeader(doc, y, brandColor);
        
        let rowsOnThisPage = 0;
        while (gameTapeIndex < gameTapeData.length && y < PAGE_HEIGHT - 80) {
            const rowHeight = drawGameTapeRow(doc, gameTapeData[gameTapeIndex], y, brandColor);
            y += rowHeight;
            gameTapeIndex++;
            rowsOnThisPage++;
        }
        console.log(`Game Tape page ${pageNum}: drew ${rowsOnThisPage} rows, remaining: ${gameTapeData.length - gameTapeIndex}`);
    }
    
    console.log(`Game Tape complete: processed ${gameTapeIndex} of ${gameTapeData.length} rows`);
    console.log(`Final PDF has ${doc.internal.getNumberOfPages()} pages`);
    
    // PARTICIPANTS section
    console.log('Drawing participants section...');
    const participantsData = witnesses || [];
    
    // Always show the PARTICIPANTS section, even if empty
    // Check if we need a new page for participants
    if (y > PAGE_HEIGHT - 100) {
        doc.addPage();
        pageNum++;
        y = drawPageHeader(doc, event, appCopy, pageNum, totalPages, logoBase64, brandColor);
    }
    
    y = drawSectionTitle(doc, 'PARTICIPANTS', 'participants', MARGIN, y + 25, false, icons);
    
    if (participantsData.length > 0) {
        
        // Group participants into rows of 3
        let participantIndex = 0;
        
        while (participantIndex < participantsData.length) {
            // Check if we need a new page
            if (y > PAGE_HEIGHT - 60) {
                doc.addPage();
                pageNum++;
                y = drawPageHeader(doc, event, appCopy, pageNum, totalPages, logoBase64, brandColor);
                y = drawSectionTitle(doc, 'PARTICIPANTS', 'participants', MARGIN, y, true, icons);
            }
            
            // Get next 3 participants
            const rowNames = [
                participantsData[participantIndex] || '',
                participantsData[participantIndex + 1] || '',
                participantsData[participantIndex + 2] || ''
            ];
            
            const rowHeight = drawParticipantsRow(doc, rowNames, y);
            y += rowHeight;
            participantIndex += 3;
        }
        
        console.log(`Participants complete: processed ${participantIndex} of ${participantsData.length} participants`);
    } else {
        // Draw empty state message
        doc.setFontSize(10);
        doc.setTextColor(BLACK_RGB.r, BLACK_RGB.g, BLACK_RGB.b);
        doc.setFont('helvetica', 'italic');
        doc.text('No participants have joined this event yet.', MARGIN, y + 20);
    }
    
    const finalPageCount = doc.internal.getNumberOfPages();
    console.log(`=== PDF Generation Complete ===`);
    console.log(`Total pages in PDF: ${finalPageCount}`);
    console.log(`Game Tape rows: ${gameTapeData.length}`);
    console.log(`Participants: ${participantsData.length}`);
    
    return doc;
  } catch (error) {
    console.error('Error in generateEventSummaryPDF:', error);
    console.error('Error stack:', error.stack);
    throw error;
  }
}
